import { logger } from '@/lib/logger';

import { throwError } from '../apiError';
import { resolveBaseUrl } from './base-url';
import { getOrigin, getBrowserSessionId } from './env';
import { hasContentTypeHeader, mergeHeaders, hasHeader } from './headers';
import { getEffectiveLocale } from './locale';
import { resolveTenantId } from './tenant';
import { getSessionAccessToken } from './token';

import type { ApiFetchOptions, AuthMode } from './types';

export type { ApiFetchOptions, AuthMode } from './types';

export { getBrowserApiConfig, setBrowserApiConfig } from './config';

const TENANT_HEADER = 'X-TENANT-ID' as const;

export const apiFetch = async <T>(
  url: string,
  opts: ApiFetchOptions = {},
): Promise<T> => {
  const origin = await getOrigin();
  const locale = (await getEffectiveLocale(opts.locale, opts.headers)) ?? 'en';

  // decide auth policy early (tenant resolution may depend on session)
  const authMode: AuthMode = opts.auth ?? 'auto';

  const baseHeaders: Record<string, string> = {
    Origin: origin,
    'Accept-Language': locale,
  };

  // browser session id
  const sid = getBrowserSessionId();
  if (sid) baseHeaders['X-B2B-SESSION-ID'] = sid;

  /**
   * 1) Resolve base URL FIRST.
   * In the new 2-phase tenancy model, base-url selection is a host concern,
   * not dependent on X-TENANT-ID being present.
   *
   * We still pass opts.tenantId (if supplied) as a hint for server-side use,
   * but browser should derive from window.location.host when config not set.
   */

  const base = await resolveBaseUrl(url, opts.headers, opts.tenantId ?? null);

  /**
   * 2) Resolve and attach tenant header (only if caller didn't already supply it).
   * This is routing/ACL downstream, not base-url selection.
   */
  const callerHasTenant =
    hasHeader(opts.headers, 'x-tenant-id') ||
    hasHeader(opts.headers, 'x-tenantid') ||
    hasHeader(opts.headers, TENANT_HEADER);

  if (!callerHasTenant) {
    const tenantId = await resolveTenantId({
      tenantId: opts.tenantId,
      headers: opts.headers,
      // IMPORTANT: do not touch SuperTokens Session when auth is 'none'
      allowSession: authMode !== 'none',
    });

    if (tenantId) baseHeaders[TENANT_HEADER] = tenantId;
  }

  // 3) auth policy
  if (authMode !== 'none') {
    const token = await getSessionAccessToken();

    // required = must have token before request
    if (authMode === 'required' && !token) {
      throw new Error('AUTH_REQUIRED');
    }
    if (token) baseHeaders.Authorization = `Bearer ${token}`;
  }

  // caller headers override ours
  const headers = mergeHeaders(baseHeaders, opts.headers);

  // default content-type only when appropriate
  const hasBody = opts.body !== undefined && opts.body !== null;
  const bodyIsFormData =
    typeof FormData !== 'undefined' && opts.body instanceof FormData;

  if (hasBody && !bodyIsFormData && !hasContentTypeHeader(opts.headers)) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  }

  // omit requested headers
  if (opts.omitHeaders?.length) {
    for (const k of opts.omitHeaders) delete headers[k];
  }

  const completeEP = `${base}${url}`;

  const response = await fetch(completeEP, { ...opts, headers });

  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');

  if (!response.ok) {
    const errorData = isJson ? await response.json().catch(() => null) : null;

    const method = (opts.method || 'GET').toUpperCase();
    logger.error(new Error(`API ${method} ${url} failed`), {
      method,
      status: response.status,
      response: errorData,
      error: response.statusText,
    });

    throwError(response.status, response.statusText, errorData);
  }

  if (response.status === 204 || !isJson) return {} as T;

  return (await response.json()) as T;
};

export const get = <T>(url: string, opts: ApiFetchOptions = {}) =>
  apiFetch<T>(url, { ...opts, method: 'GET' });

export const post = <T>(
  url: string,
  body: unknown,
  opts: ApiFetchOptions = {},
) =>
  apiFetch<T>(url, {
    ...opts,
    method: 'POST',
    body: typeof body === 'string' ? body : JSON.stringify(body),
    headers: { ...(opts.headers || {}), 'Content-Type': 'application/json' },
  });
